import dynamic from "next/dynamic";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { DateRange } from "react-day-picker";

import { getAnalytics } from "@tm/insurance/analytics";
import {
  addDaysToDate,
  formatDate,
  hydrateVars,
  isMyPartner,
  replaceHistory,
} from "@tm/insurance/utils";
import { DATE_FORMATS, TRAVELLER_NUMBER_CONSTANTS } from "@tm/isomorphic-utils";
import { ReactPortalComp } from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";

import {
  convertDateToUnix,
  convertUnixToDate,
  getIndiaTodayDate,
} from "../../utils";
import { TextNodeType } from "../FormattedText/FormattedText";
import TravelDateWiget from "./TravelDateWiget";

import { trackEvent, updateSearchContext } from "@tm/insurance/analytics/xdm";
import { theme } from "@tm/utility/global";
import { IPlanSegment } from "../InsurancePlans/type";
import WebToast from "../Toast/WebToast";
import { ITravellerInfoSection } from "../TravelDetails/types";
import TravAgeRangenCountPicker from "./TravAgeRangenCountPicker";
import TravellerBookingForWidget from "./TravellerBookingForWidget";
import TravellerCounterWiget from "./TravellerCounterWiget";
import TravellerCounterWigetV2 from "./TravellerCounterWigetV2";
import { TravellingToWidgetStyle } from "./TravellingToWidget.style";
// import TravellerCountInput from "../TravellerCountInput/TravellerCountInput";

// Dynamic load date picker components for code splitting
const DatePickerComp = dynamic(
  () => import("../DatePickerComp/DatePickerComp"),
  { ssr: false }
);

const RangeDatePicker = dynamic(
  () => import("../RangeDatePicker/RangeDatePicker"),
  { ssr: false }
);

const analytics = getAnalytics();
interface TravellingToWidgetProps {
  travelPurposeTxt?: string;
  travCountTitle?: TextNodeType | string;
  travCountSubtitle?: TextNodeType | string;
  travCountLimit?: {
    max?: number;
    min?: number;
  };
  travPurpTitle?: string;
  travDateTitle?: TextNodeType;
  btnDisabledState?: boolean;
  selectedSegment?: IPlanSegment;
  lastCalendarTypeSingle?: boolean;
  calendarDatesLimit: {
    endDateErrorMessage: string;
    insPurchaseDateLimitInDays: number;
    insPurchaseEndDateLimitInDays: number;
    insPurchaseStartDateLimitInDays: number;
    insPurchaseStartDateMinLimitInDays: number;
    startDateErrorMessage: string;
    endLesserThanStartErrorMessage: string;
    insPurchaseEndDateMinLimitInDays?: number;
  };
  countValue: number;
  setCountValue: Dispatch<SetStateAction<number>>;
  travAgeRange?: any[];
  setTravAgeRange: Dispatch<SetStateAction<any[]>>;
  setCalenderValue: Dispatch<SetStateAction<undefined | DateRange>>;
  calenderRef?: HTMLDivElement;
  upcomingTravellerNum?: number;
  upcomingFromDate?: number;
  upcomingToDate?: number;
  setIsAgeRangeSelectedError: Dispatch<SetStateAction<boolean>>;
  travellerInfoSection?: ITravellerInfoSection;
  query?: any;
  calendarConfig?: {
    insPurchaseEndDateLimitInDays: number;
    endDateErrorMessage: string;
    insPurchaseDateLimitInDays: number;
    insPurchaseStartDateLimitInDays: number;
    insPurchaseStartDateMinLimitInDays: number;
    insPurchaseEndDateMinLimitInDays?: number;
    startDateErrorMessage: string;
    clickEventName: "student_segment_clicked" | "regular_segment_clciked" | any;
    durationAllowed?: number;
    bottomBarDescription?: string;
    bottomBarIcon?: string;
  };
  travellingToRef?: React.RefObject<HTMLDivElement>;
  travellerBookingFor?: any;
  // travellerCountInput?: {
  //   subTitle?: string;
  //   max?: number;
  //   min?: number;
  // };
  travCounterTitle?: {
    title: string;
  };
}

const TravellingToWidget = ({
  travelPurposeTxt,
  travCountTitle,
  travCountSubtitle,
  travCountLimit,
  travPurpTitle,
  travDateTitle,
  btnDisabledState,
  countValue,
  setCountValue,
  calendarDatesLimit,
  setCalenderValue,
  calenderRef,
  travAgeRange,
  setTravAgeRange,
  upcomingTravellerNum,
  upcomingFromDate,
  upcomingToDate,
  setIsAgeRangeSelectedError,
  calendarConfig,
  travellerInfoSection,
  travellingToRef,
  travellerBookingFor,
  travCounterTitle,
  selectedSegment,
  lastCalendarTypeSingle,
  query,
}: // travellerCountInput,
TravellingToWidgetProps) => {
  const maxTravAllowed =
    travCountLimit?.max ||
    (isMyPartner() ? 20 : TRAVELLER_NUMBER_CONSTANTS.MAX_TRAVELLERS);
  const minTravAllowed =
    travCountLimit?.min || TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS;
  const {
    endDateErrorMessage,
    insPurchaseDateLimitInDays, //total no. of allowed days from start date (for end date)
    insPurchaseEndDateLimitInDays, //max no. of allowed days from start date (for end date)
    insPurchaseStartDateLimitInDays, //max no. of allowed days from current date (for start date)
    insPurchaseStartDateMinLimitInDays, //min no. of days from current date (for start date)
    startDateErrorMessage,
    insPurchaseEndDateMinLimitInDays,
  } = (calendarConfig ? calendarConfig : calendarDatesLimit) || {};

  // Extract durationAllowed from calendarConfig if it exists
  const durationAllowed = calendarConfig?.durationAllowed;

  const minStartDate = addDaysToDate(
    getIndiaTodayDate(),
    insPurchaseStartDateMinLimitInDays
  );

  const getToDate = () => {
    if (query?.endDate) {
      return convertUnixToDate(parseInt(query?.endDate));
    }
    if (durationAllowed) {
      return addDaysToDate(getIndiaTodayDate(), durationAllowed);
    }
    return addDaysToDate(getIndiaTodayDate(), insPurchaseEndDateMinLimitInDays);
  };

  const defaultSelected = (): DateRange => {
    return {
      from: query?.startDate
        ? convertUnixToDate(parseInt(query?.startDate))
        : addDaysToDate(
            getIndiaTodayDate(),
            insPurchaseStartDateMinLimitInDays
          ),
      to: getToDate(),
    };
  };

  const maxEndDate = addDaysToDate(
    getIndiaTodayDate(),
    insPurchaseDateLimitInDays
  );

  const device = useBreakpointV2();

  const ref = useRef<HTMLDivElement | null>(null);
  const ageRangeSelectRef = useRef<HTMLDivElement | null>(null);
  const divRefs = useRef<((instance: HTMLDivElement | null) => void)[]>([]);
  const userDateChanged = useRef<boolean>(false);

  const [captureCount, setCaptureCount] = useState(1);
  const [dateTab, setDateTab] = useState(0);
  const [range, setRange] = useState<DateRange | undefined>(defaultSelected());
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [startDate, setStartDate] = useState(getIndiaTodayDate());
  const [endDate, setEndDate] = useState<Date>();
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [isAgeRangeSelected, setIsAgeRangeSelected] = useState<boolean>(false);
  const [ageRangeSelectIndex, setAgeRangeSelectIndex] = useState<number>(-1);
  const [showDateChangeToast, setShowDateChangeToast] =
    useState<boolean>(false);
  const [dateChangeToastMessage, setDateChangeToastMessage] =
    useState<any>(null);
  const prevFocusedIndexRef = useRef(focusedIndex);

  useEffect(() => {
    const minThresholdDate = addDaysToDate(
      getIndiaTodayDate(),
      insPurchaseStartDateMinLimitInDays
    );

    const queryParams = new URLSearchParams(window.location.search);
    const startDateParam = queryParams.get("startDate");
    const endDateParam = queryParams.get("endDate");
    let newFromDate = minThresholdDate;

    if (
      newFromDate &&
      selectedSegment?.calenderSelectionType === "SINGLE" &&
      durationAllowed
    ) {
      const fromDate = range?.from || getIndiaTodayDate();
      if (startDateParam) {
        newFromDate = convertUnixToDate(parseInt(startDateParam));
      }

      const toDate = addDaysToDate(newFromDate, durationAllowed);
      if (
        toDate &&
        range?.to &&
        formatDate(toDate, DATE_FORMATS["DD/MM/YYYY"]) !==
          formatDate(range?.to, DATE_FORMATS["DD/MM/YYYY"])
      ) {
        setShowDateChangeToast(true);
        const hydratedSegment = hydrateVars(selectedSegment?.toast?.message, {
          END_DATE: formatDate(toDate, DATE_FORMATS["DD MMM YYYY"]),
        });
        setDateChangeToastMessage(hydratedSegment);
      }
      replaceDateInQuery({ startDate: newFromDate, endDate: toDate });
      setRange({
        from: newFromDate,
        to: toDate,
      });
    } else {
      if (startDateParam) {
        newFromDate = convertUnixToDate(parseInt(startDateParam));
      }

      let toDate = newFromDate
        ? addDaysToDate(newFromDate, insPurchaseEndDateMinLimitInDays)
        : addDaysToDate(getIndiaTodayDate(), insPurchaseEndDateMinLimitInDays);

      if (
        endDateParam &&
        !lastCalendarTypeSingle &&
        selectedSegment?.calenderSelectionType !== "SINGLE"
      ) {
        toDate = convertUnixToDate(parseInt(endDateParam));
      }
      if (
        toDate &&
        range?.to &&
        formatDate(toDate, DATE_FORMATS["DD/MM/YYYY"]) !==
          formatDate(range?.to, DATE_FORMATS["DD/MM/YYYY"])
      ) {
        setShowDateChangeToast(true);
        const hydratedSegment = hydrateVars(selectedSegment?.toast?.message, {
          END_DATE: formatDate(toDate, DATE_FORMATS["DD MMM YYYY"]),
        });
        setDateChangeToastMessage(hydratedSegment);
      }

      replaceDateInQuery({ startDate: newFromDate, endDate: toDate });
      setRange({
        from: newFromDate,
        to: toDate,
      });
    }
  }, [selectedSegment]);

  useEffect(() => {
    if (showDateChangeToast) {
      setTimeout(() => {
        setShowDateChangeToast(false);
      }, 3000);
    }
  }, [showDateChangeToast]);

  const replaceDateInQuery = ({
    startDate,
    endDate,
  }: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  }) => {
    if (startDate) {
      replaceHistory("startDate", convertDateToUnix(startDate).toString());
    }
    if (endDate) {
      replaceHistory("endDate", convertDateToUnix(endDate).toString());
    }
  };

  const handleClickOutside = (event: Event) => {
    if (ref?.current && !ref?.current?.contains(event.target as Node)) {
      setFocusedIndex(-1);
      setShowToast(false);
    }
    if (
      ageRangeSelectRef?.current &&
      !ageRangeSelectRef?.current?.contains(event.target as Node)
    ) {
      setAgeRangeSelectIndex(-1);
    }
  };

  const handleWindowScroll = (e: any) => {
    if (ageRangeSelectRef.current) {
      const rect = ageRangeSelectRef.current.getBoundingClientRect();

      if (
        e.target !== ageRangeSelectRef.current &&
        (window.scrollY < rect.top || window.scrollY > rect.bottom)
      ) {
        setAgeRangeSelectIndex(-1);
      }
    }
  };

  const addTravellerNumber = (travNumber: number) => {
    if (travNumber > maxTravAllowed || travNumber < minTravAllowed) {
      setCaptureCount(minTravAllowed);
      replaceHistory("travellers", minTravAllowed.toString());
    } else {
      setCaptureCount(travNumber);
      replaceHistory("travellers", travNumber.toString());
    }
  };

  const addTravellerDates = (travStartDate: number, travEndDate: number) => {
    const tempFromDate = convertUnixToDate(travStartDate);
    const tempToDate = convertUnixToDate(travEndDate);
    replaceHistory("startDate", travStartDate.toString());
    replaceHistory("endDate", travEndDate.toString());

    setRange({ from: tempFromDate, to: tempToDate });
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    document.addEventListener("scroll", handleWindowScroll, false);
    return () => {
      document.removeEventListener("scroll", handleClickOutside, false);
      document.removeEventListener("click", handleWindowScroll, false);
    };
  }, []);

  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const trav = queryParameters.get("travellers");
    const fromDate = queryParameters.get("startDate");
    const toDate = queryParameters.get("endDate");
    const travellerAgeRangesIndex = JSON.parse(
      queryParameters.get("travellerAgeRanges") ?? "[]"
    );
    if (trav && !Number.isNaN(parseInt(trav))) {
      addTravellerNumber(parseInt(trav));
    } else if (upcomingTravellerNum) {
      addTravellerNumber(upcomingTravellerNum);
    }

    const diff =
      (trav && !Number.isNaN(parseInt(trav)) ? parseInt(trav) : 1) -
      travellerAgeRangesIndex.length;

    const newTravAgeRange: any[] = [];

    const ageRange =
      travellerInfoSection?.travellerInfoSelectSection?.ageRangeSection
        ?.ageRanges;

    travellerAgeRangesIndex.forEach((item: number, index: number) => {
      newTravAgeRange.push({
        value: [ageRange?.[item]?.min, ageRange?.[item]?.max],
        label: ageRange?.[item]?.label,
        key: item,
      });
    });
    if (diff >= 0) {
      for (let i = 0; i < diff; i++) {
        newTravAgeRange.push({});
      }
    } else {
      newTravAgeRange.splice(diff, -diff);
    }
    setTravAgeRange?.(newTravAgeRange);

    if (fromDate && toDate) {
      addTravellerDates(parseInt(fromDate), parseInt(toDate));
    } else if (upcomingFromDate && upcomingToDate) {
      addTravellerDates(upcomingFromDate, upcomingToDate);
    }
  }, []);
  useEffect(() => {
    setEndDate(addDaysToDate(getIndiaTodayDate(), 1));
  }, []);

  useEffect(() => {
    if (captureCount >= minTravAllowed) {
      setCountValue(captureCount);
    }
  }, [captureCount]);

  useEffect(() => {
    if (range) {
      setCalenderValue(range);
    }
  }, [range]);

  const checkIsAgeRangeSelected = (travAgeRange: any[]) => {
    if (travAgeRange.length > 0) {
      var isAgenCountApply = travAgeRange.length === captureCount;
      travAgeRange?.forEach((item) => {
        if (!item.value) {
          isAgenCountApply = false;
        }
      });
      setIsAgeRangeSelected(isAgenCountApply);
    }
  };

  useEffect(() => {
    checkIsAgeRangeSelected(travAgeRange ?? []);
  }, [travAgeRange]);

  useEffect(() => {
    const datePickerElement = document.getElementById("datePickerContainer");

    if (datePickerElement) {
      const rect = datePickerElement.getBoundingClientRect();
      const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (prevFocusedIndexRef.current === 0 && focusedIndex !== 0) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (focusedIndex === 0 && !isInView) {
        const offset = rect.top + rect.height / 2 - window.innerHeight / 2;
        window.scrollTo({ top: offset, behavior: "smooth" });
      }
    }

    prevFocusedIndexRef.current = focusedIndex;
  }, [focusedIndex]);

  useEffect(() => {
    if (range?.to === undefined && range?.from) {
      let tempToDate = new Date(range?.from);
      tempToDate.setDate(tempToDate.getDate() + 1);

      replaceHistory("endDate", convertDateToUnix(tempToDate).toString());
    }
  }, [range?.from]);

  const handleClick = (index: number) => {
    if (index === focusedIndex) {
      setFocusedIndex(-1);
      setShowToast(false);
    } else {
      setFocusedIndex(index);
      if (index === 3) {
        analytics?.trackLandingEvents(
          {
            actionType: "Ins_TravellerAndAgeDropDownClicked",
          }
          //"click"
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "Ins_TravellerAndAgeDropDownClicked",
          },
        });
      } else if (index === 0) {
        analytics?.trackLandingEvents(
          {
            actionType: "Ins_TravelDateDropDownClicked",
          }
          //"click"
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "Ins_TravelDateDropDownClicked",
          },
        });
      }
    }
  };

  const handleDayClick = (selectedDate: Date) => {
    if (dateTab === 0) {
      const maxStartDate = addDaysToDate(
        getIndiaTodayDate(),
        insPurchaseStartDateLimitInDays
      );

      if (maxStartDate && maxStartDate < selectedDate) {
        // alert(startDateErrorMessage);
        setShowToast(true);
        setToastMessage(startDateErrorMessage);
      } else if (range?.from && range?.to) {
        setRange({ from: selectedDate, to: undefined });
        replaceHistory("startDate", convertDateToUnix(selectedDate).toString());
        setDateTab(1);
        setShowToast(false);
      }
    } else if (dateTab === 1) {
      if (range?.from) {
        const fromDate = new Date(range?.from);
        const maxEndDate = addDaysToDate(
          fromDate,
          calendarConfig?.insPurchaseEndDateLimitInDays
        );
        if (range?.from > selectedDate) {
          setRange({ from: selectedDate, to: undefined });
          setShowToast(false);
        } else if (maxEndDate && maxEndDate <= selectedDate) {
          setShowToast(true);
          setToastMessage(endDateErrorMessage);
        } else {
          setRange({ from: range?.from, to: selectedDate });
          setShowToast(false);
          setDateTab(0);
          if (device == "lg") {
            setFocusedIndex(1);
            analytics?.trackLandingEvents(
              {
                actionType: "travel_date_selected",
                payload: {
                  departure: range.from
                    ?.toLocaleDateString("en-GB")
                    ?.replace(/\//gi, ":"),
                  arrival:
                    selectedDate
                      ?.toLocaleDateString("en-GB")
                      ?.replace(/\//gi, ":") || "",
                },
              }
              //"click"
            );
            trackEvent(
              {
                event_detail: {
                  event_type: "action",
                  event_name: "component_cta-clicked",
                  event_value: "travel_date_selected",
                },
              },
              {
                fromDateTime: convertDateToUnix(range?.from),
                toDateTime: convertDateToUnix(selectedDate),
              }
            );
          }
          replaceHistory("endDate", convertDateToUnix(selectedDate).toString());
        }
      }
    }
  };

  const handleCaptureCountIncr = () => {
    captureCount < maxTravAllowed
      ? setCaptureCount(captureCount + 1)
      : setCaptureCount(maxTravAllowed);

    updateSearchContext({
      travellers:
        captureCount < maxTravAllowed ? captureCount + 1 : maxTravAllowed,
    });
    replaceHistory("travellers", (captureCount + 1).toString());
  };
  const handleCaptureCountDecr = () => {
    captureCount > minTravAllowed
      ? setCaptureCount(captureCount - 1)
      : setCaptureCount(minTravAllowed);

    // if (device == 'lg') {
    //     setFocusedIndex(2);
    // }
    updateSearchContext({
      travellers:
        captureCount > TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS
          ? captureCount - 1
          : TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS,
    });
    replaceHistory("travellers", (captureCount - 1).toString());
  };

  // const handleTravellerCountChange = (count: number) => {
  //   setCaptureCount(count);
  //   updateSearchContext({
  //     travellers: count,
  //   });
  //   replaceHistory("travellers", count.toString());
  // };

  const handleSingleDateSelect = (selectedDate: Date) => {
    // Validate selected date against max start date
    const maxStartDate = addDaysToDate(
      getIndiaTodayDate(),
      insPurchaseStartDateLimitInDays
    );

    if (maxStartDate && maxStartDate < selectedDate) {
      setShowToast(true);
      setToastMessage(startDateErrorMessage);
      return;
    }

    // Auto-calculate end date based on business logic
    let autoEndDate: Date;
    if (
      durationAllowed &&
      selectedSegment?.calenderSelectionType === "SINGLE"
    ) {
      // For multi-trip, use the duration allowed
      autoEndDate =
        addDaysToDate(selectedDate, durationAllowed) || selectedDate;
    } else {
      // For single trip, use the minimum end date limit
      autoEndDate =
        addDaysToDate(selectedDate, insPurchaseEndDateMinLimitInDays) ||
        selectedDate;
    }

    // Validate auto-calculated end date against max end date
    const maxAllowedEndDate = addDaysToDate(
      getIndiaTodayDate(),
      insPurchaseDateLimitInDays
    );
    if (maxAllowedEndDate && maxAllowedEndDate < autoEndDate) {
      // If auto-calculated end date exceeds limit, set to max allowed
      autoEndDate = maxAllowedEndDate;
    }

    // Set the date range with auto-calculated end date
    setRange({ from: selectedDate, to: autoEndDate });
    setShowToast(false);

    // Update URL history
    replaceHistory("startDate", convertDateToUnix(selectedDate).toString());
    replaceHistory("endDate", convertDateToUnix(autoEndDate).toString());

    // Track analytics
    analytics?.trackLandingEvents({
      actionType: "travel_date_selected",
      payload: {
        departure: selectedDate
          ?.toLocaleDateString("en-GB")
          ?.replace(/\//gi, ":"),
        arrival:
          autoEndDate?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") || "",
      },
    });
    trackEvent(
      {
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "travel_date_selected",
        },
      },
      {
        fromDateTime: convertDateToUnix(selectedDate),
        toDateTime: convertDateToUnix(autoEndDate),
      }
    );

    // Auto-close calendar and move to next step
    setFocusedIndex(1); // Move to next widget (travellers)
    setDateTab(0); // Reset to start date tab for next time
  };

  const handleSubmit = (chkIn: Date | undefined, chkOut: Date | undefined) => {
    chkIn && setStartDate(chkIn);
    chkOut && setEndDate(chkOut);

    analytics?.trackLandingEvents(
      {
        actionType: "travel_date_selected",
        payload: {
          departure:
            chkIn?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") || "",
          arrival:
            chkOut?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") || "",
        },
      }
      //"click"
    );
    trackEvent(
      {
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "travel_date_selected",
        },
      },
      {
        fromDateTime: chkIn ? convertDateToUnix(chkIn) : 0,
        toDateTime: chkOut ? convertDateToUnix(chkOut) : 0,
      }
    );
    setFocusedIndex(-1);
    setShowToast(false);
  };

  const isSingleDate = selectedSegment?.calenderSelectionType === "SINGLE";

  return (
    <TravellingToWidgetStyle ref={device == "lg" ? ref : null}>
      <TravelDateWiget
        ref={divRefs.current[0]}
        showTravDate={focusedIndex == 0}
        handleTravDate={() => handleClick(0)}
        travDateTitle={travDateTitle}
        startDate={range?.from}
        endDate={range?.to}
      />
      {travellerBookingFor ? (
        <TravellerBookingForWidget
          travellerBookingFor={travellerBookingFor}
          captureCount={captureCount}
          travCounterTitle={travCounterTitle}
          setCaptureCount={setCaptureCount}
        />
      ) : travellerInfoSection ? (
        <TravellerCounterWigetV2
          countValue={countValue}
          showSelectSection={focusedIndex === 3}
          handleSelectSection={() => handleClick(3)}
          travSelectTitle={travellerInfoSection?.selectTitle}
          postSelectTitle={travellerInfoSection?.postSelectTitle}
          postSelectDesc={travellerInfoSection?.postSelectDesc}
          travCountTitle={travellerInfoSection?.title}
          description={travellerInfoSection?.description}
          isAgeRangeSelected={isAgeRangeSelected}
          travAgeRange={travAgeRange}
        />
      ) : (
        //TODO: Use this component post design finalization
        // ) : travellerCountInput ? (
        //   <TravellerCountInput
        //     value={captureCount}
        //     onChange={handleTravellerCountChange}
        //     min={travellerCountInput?.min || minTravAllowed}
        //     max={travellerCountInput?.max || maxTravAllowed}
        //     subTitle={travellerCountInput?.subTitle}
        //   />
        <TravellerCounterWiget
          // ref={divRefs.current[1]}
          // showTravCount={focusedIndex == 1}
          captureCount={captureCount}
          maxTravAllowed={maxTravAllowed}
          minTravAllowed={minTravAllowed}
          travCountTitle={travCountTitle}
          handleCaptureCountIncr={handleCaptureCountIncr}
          handleCaptureCountDecr={handleCaptureCountDecr}
          travCountSubtitle={travCountSubtitle}
        />
      )}

      {/* TODO: NOT IN CURRENT SCOPE */}
      {/* <TravelPurposeWiget
                ref={(el: HTMLDivElement | null) => (divRefs.current[2] = el)}
                showTravPurpose={focusedIndex == 2}
                handleTravPurpose={() => handleClick(2)}
                capturePurpose={capturePurpose}
                travPurpTitle={travPurpTitle}
            /> */}

      {focusedIndex == 0 && device == "lg" && (
        <DatePickerComp
          startDateLabel="Start Date"
          endDateLabel="End Date"
          handleDayClick={handleDayClick}
          dateTab={dateTab}
          selected={range}
          from={range?.from}
          to={range?.to}
          maxEndDate={maxEndDate}
          minStartDate={minStartDate}
          toastMessage={toastMessage}
          showToast={showToast}
          isSingleDate={isSingleDate}
          calenderHeader={selectedSegment?.calenderHeader}
          onSingleDateSelect={handleSingleDateSelect}
        />
      )}
      {focusedIndex == 0 && device == "sm" && (
        <ReactPortalComp>
          <RangeDatePicker
            calendarFooter={true}
            calendarHeader={true}
            onBackPress={() => handleClick(0)}
            onSubmitButtonPress={handleSubmit}
            range={true}
            calenderRef={calenderRef}
            from={range?.from}
            to={range?.to}
            toastMessage={toastMessage}
            showToast={showToast}
            selected={range}
            handleDayClick={handleDayClick}
            maxEndDate={maxEndDate}
            minStartDate={minStartDate}
            isSingleDate={isSingleDate}
            bottomBarDescription={selectedSegment?.config?.bottomBarDescription}
            bottomBarIcon={selectedSegment?.config?.bottomBarIcon}
            onSingleDateSelect={handleSingleDateSelect}
          />
        </ReactPortalComp>
      )}
      {focusedIndex === 3 && device === "lg" && (
        <TravAgeRangenCountPicker
          ageRangeSelectRef={ageRangeSelectRef}
          travellerInfoSelectSection={
            travellerInfoSection?.travellerInfoSelectSection
          }
          captureCount={captureCount}
          setTravAgeRange={setTravAgeRange}
          setCaptureCount={setCaptureCount}
          setIsAgeRangeSelectedError={setIsAgeRangeSelectedError}
          focusedIndex={focusedIndex}
          travAgeRange={travAgeRange ?? []}
          ageRangeSelectIndex={ageRangeSelectIndex}
          setAgeRangeSelectIndex={setAgeRangeSelectIndex}
          setFocusedIndex={setFocusedIndex}
          travellingToRef={travellingToRef}
        />
      )}
      {focusedIndex === 3 && device === "sm" && (
        <ReactPortalComp>
          <TravAgeRangenCountPicker
            ageRangeSelectRef={ageRangeSelectRef}
            travellerInfoSelectSection={
              travellerInfoSection?.travellerInfoSelectSection
            }
            captureCount={captureCount}
            setTravAgeRange={setTravAgeRange}
            setIsAgeRangeSelectedError={setIsAgeRangeSelectedError}
            setCaptureCount={setCaptureCount}
            focusedIndex={focusedIndex}
            travAgeRange={travAgeRange ?? []}
            ageRangeSelectIndex={ageRangeSelectIndex}
            setAgeRangeSelectIndex={setAgeRangeSelectIndex}
            setFocusedIndex={setFocusedIndex}
          />
        </ReactPortalComp>
      )}
      {dateChangeToastMessage && showDateChangeToast && (
        <WebToast
          type="warning"
          iconUrl={selectedSegment?.toast?.iconUrl}
          toastTxt={dateChangeToastMessage}
          position="fixed"
          toastTxtMargin="0px 0px 0px 5px"
          color={theme.yellowText}
          fontSize="14px"
          fontWeight="700"
          right="50px"
          bottom={device === "sm" ? "70px" : "50px"}
        />
      )}
      {/* {focusedIndex == 1 && device == 'lg' && (
                <WidgetList travelCount>
                    {Array.from({ length: 20 }, (__, index) => (
                        <ListItem
                            key={index}
                            onClick={() => handleCaptureCount(index)}
                        >
                            <SpanTag fontSize="xs">
                                {index < 9 ? '0' + (index + 1) : index + 1}
                            </SpanTag>
                        </ListItem>
                    ))}
                </WidgetList>
            )}
            {focusedIndex == 1 && device == 'sm' && (
                <TravellerCountModal
                    captureCount={captureCount}
                    handleCaptureCount={handleCaptureCount}
                    handleClick={() => handleClick(1)}
                />
            )} */}
      {/* {focusedIndex == 2 && (
                <WidgetList width="310px">
                    <ListItem>
                        <ParaTag>{travelPurposeTxt}</ParaTag>
                    </ListItem>
                    {purposeList &&
                        purposeList.map((item, index) => {
                            return (
                                <ListItem
                                    key={index}
                                    onClick={() =>
                                        handleCapturePurpose(item.info)
                                    }
                                >
                                    {item.icon && (
                                        <ImageComponent src={item.icon} />
                                    )}
                                    <SpanTag>{item.info}</SpanTag>
                                </ListItem>
                            );
                        })}
                </WidgetList>
            )} */}
      {/* TODO */}
    </TravellingToWidgetStyle>
  );
};

export default TravellingToWidget;
